import React, { useState, useEffect  } from 'react';
import { DatesType, UnavailableDatesType, DatesSelectionType } from '../../Components/Types';
import MonthSelector from '../../Components/Calendar/MonthSelector';
import { splitArray, getDaysArray } from '../../Components/Functions';
import CalenderKey from '../../Components/Calendar/CalendarKey';
import { useMsal } from '@azure/msal-react';
import { calendarConfig, toastConfig } from 'Components/Constants';
import { getAllDays, getDaysForCalendar, highlightSelectedDates, orderedDates } from 'Components/Calendar/CalendarFunctions';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { CalendarHeader, DatesCalendarContainer, HeaderContainer } from 'Components/Calendar/CalendarComponents';
import DatesCalendar from '../../Components/Calendar/DatesCalendar';
import SummaryContainer from './SummaryContainer';
import { BankHolidaysAPI, MonthDetailsAPI } from 'API/CalendarAPI';
import { VehicleAPI } from 'API/VehicleAPI';
import Button from 'Components/Button';

type DatesCalendarProps = {
    datesSelection: DatesSelectionType;
}

function CalendarContainer({ datesSelection }: DatesCalendarProps) {
    const [selectedDates, setSelectedDates] = useState<string[]>(getDaysArray(datesSelection.start_date, datesSelection.end_date));
    const { currentEmployee } = useSelector((state: RootState) => state.auth);
    const [datesLoading, setDatesLoading] = useState<boolean>(true);
    const [hideContinue, setHideContinue] = useState<boolean>(false);
    const [days, setDays] = useState<DatesType[][]>([]);
    const [year, setYear] = useState<number>(0);
    const [month, setMonth] = useState<number>(0);
    const [cost, setCost] = useState<number>(0);
    const daysPerRow = calendarConfig.includeWeekends ? 7 : 5;
    const { instance } = useMsal();
    
    useEffect(() => {
        if (currentEmployee?.employee_id) {
            const getDaysData = async() => {
                if (month > -1 && year > 0) {
                    await getDaysInMonth(month, year);
                }
            }
            getDaysData();
        }
    }, [month, year]);

    const getDaysInMonth = async (month: number, year: number) => {
        var unavailableDates: UnavailableDatesType[] = [];
        var dailyPrices: number[] = [];
        const response = await MonthDetailsAPI.get(instance, datesSelection.vehicle_id, year, month)
        if (calendarConfig.getUnavailableDates) {
            unavailableDates = response.unavailability;
        }
        dailyPrices = response.dailyPrices;
        const days = await getDaysForCalendar(month, year, dailyPrices, unavailableDates);
        let daysInRows = splitArray(days, daysPerRow);
        if (selectedDates.length > 1 && !hideContinue) {
            daysInRows = highlightSelectedDates(selectedDates[0], selectedDates[selectedDates.length -1], daysInRows);
            await checkSelectedDates(selectedDates[0], selectedDates[selectedDates.length -1]);
        }
        setDays(daysInRows);
        setDatesLoading(false);
    }

    const checkSelectedDates = async(startDate: string, endDate: string) => {
        await getAvailability(startDate, endDate);
        const bankHolidaysYr = await BankHolidaysAPI.get();
        const bankHolidays = [...bankHolidaysYr];
        if (bankHolidays.includes(startDate) || bankHolidays.includes(endDate)) {
            setHideContinue(true);
            const message = "Motorhomes cannot be dropped off or collected on bank holidays";
            toast.error(message, { ...toastConfig, toastId: message + "-toast" });
        }
    }

    const getAvailability = async (startDate: string, endDate: string) => {
        try {
            const response = await VehicleAPI.CheckAvailability(instance, startDate, endDate, datesSelection.vehicle_id);
            setHideContinue(false);
            setCost(response);
        }
        catch {
            setCost(0);
            setHideContinue(true);
        }
    }

    const clearSelectedDates = () => {
        const daysArr = getAllDays(days);
        daysArr.forEach((day: DatesType) => {
            day.selected = false;
            day.isCollection = false;
            day.isReturn = false; 
        })
        setSelectedDates([]);
    }

    const addDate = async (newDate: string) => {
        if (selectedDates.length < 2) {
            let datesSelection = [...selectedDates, newDate];
            if (datesSelection.length > 1) {
                datesSelection = orderedDates(datesSelection[0], datesSelection[datesSelection.length -1]);
            }
            setSelectedDates(datesSelection);

            if (datesSelection.length == 1) {
                const daysInRows = await highlightSelectedDates(datesSelection[0], datesSelection[0], days);
                setDays(daysInRows);
            }
            else {
                const daysInRows = await highlightSelectedDates(datesSelection[0], datesSelection[1], days);
                await checkSelectedDates(datesSelection[0], datesSelection[1]);
                setDays(daysInRows);
            }
        }
    }

    return (
        <>
            <HeaderContainer>
                <CalendarHeader>Select Dates</CalendarHeader>
            </HeaderContainer>
            <DatesCalendarContainer>
                <MonthSelector setMonth={setMonth} setYear={setYear} datesSelection={datesSelection} />
                <DatesCalendar days={days} addDate={addDate} datesLoading={datesLoading}/>
                <CalenderKey />
                <Button 
                    text={"clear selection"} 
                    type={"button"} 
                    colour={"dark"} 
                    onClick={clearSelectedDates} 
                    disabled={selectedDates.length == 0}
                    css={{float:"left", margin: 0}}
                />
            </DatesCalendarContainer>
            <SummaryContainer selectedDates={selectedDates} hideContinueBtn={hideContinue} totalCost={cost}/>
        </>
    );
}
export default CalendarContainer;
